import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, clearTokens, hasRefreshToken, setTokens, ApiError } from '../api/client.js'

export interface SessionUser {
  id: string
  name: string
  phone: string
  preferred_language: string
  must_reset_password: boolean
  current_branch_id: string | null
  /** Permission keys, e.g. 'audit_log.read'. Used only to decide what to show; the API enforces. */
  permissions?: string[]
  /** From /auth/me — used by the TSupport widget to tag support tickets. */
  email?: string | null
  tenant_id?: string
  tenant_name?: string
}

export interface Branch {
  id: string
  name: string
  code: string
}

interface AuthState {
  user: SessionUser | null
  can: (permission: string) => boolean
  branches: Branch[]
  isAllBranches: boolean
  currentBranchId: string | null
  /** True while the initial session-restore attempt (via /auth/me) is in flight. */
  bootstrapping: boolean
  login: (tenantCode: string, phone: string, password: string) => Promise<void>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  switchBranch: (branchId: string | null) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [isAllBranches, setIsAllBranches] = useState(false)
  const [bootstrapping, setBootstrapping] = useState(true)

  const loadBranches = useCallback(async () => {
    // Stage 3 §2.1: /api/auth/branches, not the Branches MASTER — every signed-in
    // user needs this regardless of whether they hold tenant_settings permission.
    const rows = await api.get<Branch[]>('/auth/branches')
    setBranches(rows)
  }, [])

  useEffect(() => {
    ;(async () => {
      // Access tokens are memory-only (client.ts), so a page reload has nothing
      // to show until this succeeds — it doubles as "is my refresh token still
      // good" and "who am I" in one round trip.
      if (hasRefreshToken()) {
        try {
          const me = await api.get<SessionUser>('/auth/me')
          setUser(me)
          setIsAllBranches(me.current_branch_id === null)
          await loadBranches().catch(() => {})
        } catch {
          clearTokens()
        }
      }
      setBootstrapping(false)
    })()
  }, [loadBranches])

  const login = useCallback(
    async (tenantCode: string, phone: string, password: string) => {
      const result = await api.post<{
        access_token: string
        refresh_token: string
        user: Omit<SessionUser, 'current_branch_id'>
        branches: Branch[] | 'all'
        current_branch_id: string | null
      }>('/auth/login', { tenant_code: tenantCode, phone, password }, { auth: false })

      setTokens(result)
      const me = await api.get<SessionUser>('/auth/me').catch(() => null)
      setUser({ ...result.user, current_branch_id: result.current_branch_id, permissions: me?.permissions ?? [], email: me?.email ?? null, ...(me?.tenant_id ? { tenant_id: me.tenant_id } : {}), tenant_name: me?.tenant_name ?? '' })
      setIsAllBranches(result.branches === 'all')
      if (Array.isArray(result.branches)) {
        // The login response already has the list — avoid a second round trip.
        const rows = await api.get<Branch[]>('/auth/branches')
        setBranches(rows)
      } else {
        await loadBranches()
      }
    },
    [loadBranches],
  )

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {})
    clearTokens()
    setUser(null)
    setBranches([])
  }, [])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const result = await api.post<{ access_token: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    setTokens(result)
    setUser((prev) => (prev ? { ...prev, must_reset_password: false } : prev))
  }, [])

  const switchBranch = useCallback(async (branchId: string | null) => {
    const result = await api.post<{ access_token: string }>('/auth/switch-branch', { branch_id: branchId })
    setTokens(result)
    setUser((prev) => (prev ? { ...prev, current_branch_id: branchId } : prev))
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        can: (permission: string) => !!user?.permissions?.includes(permission),
        branches,
        isAllBranches,
        currentBranchId: user?.current_branch_id ?? null,
        bootstrapping,
        login,
        logout,
        changePassword,
        switchBranch,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { ApiError }
